import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';

import { CursorContainer, CursorLabel, CursorImage } from './animatedCursor.styles';
import { AnimatedCursorProps, CursorPositions, RefObject } from './animatedCursor.types';
import { animationType } from './animations';

export function AnimatedCursor({
  animation,
  elementsWithAnimatedCursor = 'canvas, #waiting',
  image,
  label,
  position = CursorPositions.UPPER_LEFT,
  rotation = 0,
  setDomUpdate,
  shouldUpdate = true,
  size = 24,
  visible = true, // review if this is necessary, visibility should be managed now only by onMouseOver
  waiting,
}: AnimatedCursorProps) {
  const cursorContainerRef: RefObject<HTMLDivElement> = useRef(null);
  const [isVisible, setIsVisible] = useState(visible);
  const [cursorRootElement, setCursorRootElement] = useState<HTMLElement | null>(null);
  const [updateNonRoot, setUpdateNonRoot] = useState(false);

  const setPosition = (clientX: number, clientY: number) => {
    switch (position) {
      case CursorPositions.UPPER_LEFT:
        return {
          x: clientX,
          y: clientY,
        };
      case CursorPositions.UPPER_RIGHT:
        return {
          x: clientX - size,
          y: clientY,
        };
      case CursorPositions.LOWER_LEFT:
        return {
          x: clientX,
          y: clientY - size,
        };
      case CursorPositions.LOWER_RIGHT:
        return {
          x: clientX - size,
          y: clientY - size,
        };
      case CursorPositions.CENTER:
      default:
        return {
          x: clientX - size / 2,
          y: clientY - size / 2,
        };
    }
  };

  const onMouseMove = useCallback(
    ({ clientX, clientY }: MouseEvent) => {
      const cursorPosition = setPosition(Number(clientX), Number(clientY));
      const { current } = cursorContainerRef;
      current && (current.style.top = `${cursorPosition.y}px`);
      current && (current.style.left = `${cursorPosition.x}px`);
    },
    [position],
  );

  window.addEventListener('mousemove', onMouseMove);

  const getDomElements = () => document.querySelectorAll(elementsWithAnimatedCursor);
  let domElementsIds = JSON.stringify(Array.from(getDomElements()).map((elem) => elem.id));

  const onMouseOver = (event: MouseEvent) => {
    const { target } = event;
    let visible = !!waiting;
    if (!visible) {
      const elements = JSON.parse(domElementsIds);
      for (let i = 0; i < elements.length; i++) {
        const target_ = target as Element;
        visible = target_?.id === elements[i];
        if (visible) break;
      }
    }
    setIsVisible(visible);
  };

  useEffect(() => {
    if (setDomUpdate) {
      domElementsIds = JSON.stringify(Array.from(getDomElements()).map((elem) => elem.id));
      setDomUpdate(false);
    }
  }, [shouldUpdate]);

  const getCursorContainerX = (): number =>
    cursorContainerRef.current ? +cursorContainerRef.current?.style.left.slice(0, -2) : 0;
  const getCursorContainerY = (): number =>
    cursorContainerRef.current ? +cursorContainerRef.current?.style.top.slice(0, -2) : 0;

  useEffect(() => {
    document.addEventListener('mouseover', onMouseOver);
    return () => {
      document.removeEventListener('mouseover', onMouseOver);
    };
  }, [waiting, domElementsIds]);

  useEffect(() => {
    setIsVisible(visible);
  }, [visible]);

  useEffect(() => {
    const { current } = cursorContainerRef;
    if (isVisible) {
      current && (current.style.opacity = `${1}`);
      document.body.style.cursor = 'none';
    } else {
      current && (current.style.opacity = `${0}`);
      document.body.style.cursor = 'default';
    }
  }, [isVisible]);

  useLayoutEffect(() => {
    const rotationInDegrees = (rotation % (2 * Math.PI)) * (180 / Math.PI);
    const { current } = cursorContainerRef;
    current &&
      (current.style.transform = `translateZ(0)  scale(1) rotate(${rotationInDegrees}deg)`);
  }, [rotation]);

  useLayoutEffect(() => {
    if (cursorContainerRef) {
      const { current } = cursorContainerRef;
      const cursorPosition = setPosition(getCursorContainerX(), getCursorContainerY());
      current && (current.style.top = `${cursorPosition.y}px`);
      current && (current.style.left = `${cursorPosition.x}px`);
    }
  }, [position]);

  useEffect(() => {
    const cursorRoot = document.getElementById('cursor-root');
    setCursorRootElement(cursorRoot);
  }, []);

  useEffect(() => {
    if (!cursorRootElement && !updateNonRoot) {
      setUpdateNonRoot(true);
    }
  }, [cursorContainerRef.current]);

  const Cursor = (
    <React.Fragment>
      <CursorImage
        id={waiting ? 'spinner' : ''}
        animation={waiting ? animationType.ROTATION : animation}
        src={image}
        alt="cursor"
      />
      {label && <CursorLabel size={size}>{label}</CursorLabel>}
    </React.Fragment>
  );
  const ContainedCursor = (
    <CursorContainer ref={cursorContainerRef} size={size}>
      {Cursor}
    </CursorContainer>
  );

  function renderCursor() {
    const { current } = cursorContainerRef;
    if (!cursorRootElement) {
      return (
        <CursorContainer ref={cursorContainerRef} size={size}>
          {current && ReactDOM.createPortal(Cursor, current)}
        </CursorContainer>
      );
    }

    return ReactDOM.createPortal(ContainedCursor, cursorRootElement);
  }

  return renderCursor();
}

AnimatedCursor.defaultProps = {
  size: 24,
  visible: true,
  rotation: 0,
  animation: undefined,
  elementsWithAnimatedCursor: 'canvas, #waiting', // Canvas elements or elements with id of `waiting`
  position: CursorPositions.UPPER_LEFT,
  shouldUpdate: true,
};
